/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import { APButton, APTypography } from '@astropay/web-core-library';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Slide,
  Typography,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import classnames from 'classnames';

import ApImage from '../ap-image/ap-image';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      margin: theme.spacing(1),
      borderRadius: theme.spacing(1),
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: { minWidth: 300, minHeight: 300 },
    '&.reduceContent': {
      [theme.breakpoints.up('md')]: { minWidth: 'auto', minHeight: 'auto' },
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '30em',
  },
  reduceContent: {
    padding: '0 !important',
  },
  image: {
    width: 90,
    height: 90,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '4em auto 2.5em',
    '& svg': {
      fontSize: '6em',
      color: theme.palette.text.secondary,
    },
  },
  icon: {
    width: 40,
    height: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '2em auto 2.5em',
    '& svg': {
      fontSize: '4em',
      '&.red': {
        color: theme.palette.primary.main,
      },
    },
  },
  text: {
    fontSize: '1rem',
    textAlign: 'center',
    lineHeight: 2,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
    '&.reduceContent': {
      margin: 0,
    },
  },
  title: {
    fontWeight: 'bold',
    fontSize: theme.spacing(2.2),
    padding: '.5em',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(1, 3),
    '& .disclaimer': {
      fontSize: theme.spacing(1.3),
      textAlign: 'center',
      fontWeight: 400,
    },
  },
  action: {
    minWidth: 150,
  },
  actionButton: {
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
    maxWidth: '200px',
    width: '100%',
  },
  closeIcon: {
    position: 'absolute',
    right: '.7em',
    top: '.7em',
    cursor: 'pointer',
    fontSize: theme.spacing(2),
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface AlertDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  content: React.ReactNode;
  iconName?: IconName;
  actions?: {
    onClick: () => void;
    text: string;
    color?: string;
    variant?: string;
    id?: string;
    fullWidth?: boolean;
  }[];
  actionsDescription?: string;
  contentComponent?: 'p' | 'div';
  img?: string;
}

const AlertDialog: React.FC<AlertDialogProps> = (props) => {
  const {
    isOpen,
    onClose,
    content,
    actions,
    contentComponent = 'p',
    title,
    img,
    iconName,
    actionsDescription,
  } = props;
  const classes = useStyles();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={classes.dialog}
    >
      {actions && (
        <FontAwesomeIcon className={classes.closeIcon} icon={['far', 'times']} onClick={onClose} />
      )}

      <Box className={classnames(classes.root, { reduceContent: !actions })}>
        <DialogContent classes={{ root: !actions ? classes.reduceContent : undefined }}>
          <Box className={classnames(classes.content, { reduceContent: !actions })}>
            {img && (
              <Box className={classes.image}>
                <ApImage src={img} alt="icon" />
              </Box>
            )}
            {iconName && (
              <Box className={classes.icon}>
                <FontAwesomeIcon className="red" icon={['far', iconName]} />
              </Box>
            )}

            {title && (
              <DialogTitle id={'alert-dialog-title'} className={classes.title} disableTypography>
                {title}
              </DialogTitle>
            )}
            <DialogContentText
              id="alert-dialog-description"
              component={contentComponent}
              className={classnames(classes.text, { reduceContent: !actions })}
            >
              <APTypography textStyle="Typography_Poppins_14px_Regular">{content}</APTypography>
            </DialogContentText>
          </Box>
        </DialogContent>

        {actions && (
          <DialogActions className={classes.actions} disableSpacing>
            {actions &&
              actions.map((action) => (
                <APButton
                  id={action.id ?? ''}
                  key={`alert-dialog-action-${action.text}`}
                  onClick={action.onClick}
                  className={actions.length === 1 ? classes.action : classes.actionButton}
                  color={action.color ? (action.color as never) : 'secondary'}
                  variant={action.variant ? (action.variant as never) : 'flat'}
                  style={{ maxWidth: `${action.fullWidth && '100%'}` }}
                >
                  {action.text}
                </APButton>
              ))}
            {actionsDescription && (
              <Typography variant="inherit" className="disclaimer">
                {actionsDescription}
              </Typography>
            )}
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
};

export default AlertDialog;
